import { MouseEvent } from 'react';
import { Box, DataTable, Text } from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { actions, Can, subjects } from '@rideiq/contexts/AbilityContext';
import { useFlags } from '@rideiq/contexts/LDContext';
import {
  getCheckboxColumn,
  getShowCheckboxes,
  getSortableColumns,
  invoiceGroupColumns,
} from '@rideiq/riders/features/RidersTable/helpers';
import type { RidersQueryParams } from '@rideiq/riders/types';
import { Button } from '@rideiq/shared/components/Button';
import { QueryErrorState } from '@rideiq/shared/components/QueryErrorState';
import { TablePaginationContainer } from '@rideiq/shared/components/TablePaginationContainer';
import { colorPalette, variables } from '@rideiq/shared/components/variables';
import { SortState } from '@rideiq/shared/sortFilter';

import CreateRider from '../CreateRider';

interface RidersTableProps {
  currentStatusTab: string;
  handleRiderClick: (rider: RiderForList) => void;
  tableHeightOffset: number;
  selectedRiderIds: number[];
  handleCheckboxSelection: (event: MouseEvent, id: number, riderIds: number[]) => void;
  clearSelection: () => void;
  handleSelectAllOnPage: (pageRiderIds: number[]) => void;
  riders: RiderForList[];
  isLoading: boolean;
  isError: boolean;
  queryParams: RidersQueryParams;
  handlePageChange: (pageParams: { page: number; perPage: number }, isPaginated?: boolean) => void;
  handleSort: (column: string) => void;
  clearAllFilters: () => void;
  totalCount: number;
  selectedLetter?: string;
  onClearAlphabetFilter: () => void;
  fetchRiders: () => void;
}

export const RidersTable = ({
  currentStatusTab,
  tableHeightOffset,
  handleRiderClick,
  selectedRiderIds,
  handleCheckboxSelection,
  clearSelection,
  handleSelectAllOnPage,
  riders,
  isLoading,
  isError,
  queryParams,
  handlePageChange,
  handleSort,
  clearAllFilters,
  totalCount,
  selectedLetter,
  onClearAlphabetFilter,
  fetchRiders,
}: RidersTableProps) => {
  const { isAdminOrOrganizerUser } = useAuthQuery();
  const { enableRidersAdvancedSorting, enableBulkRiderStatusChange } = useFlags();
  const showCheckboxes = getShowCheckboxes(isAdminOrOrganizerUser, currentStatusTab, enableBulkRiderStatusChange);

  const pageRiderIds = riders.map((r) => r.id);

  const handlePageChangeWithReset = (pageParams: { page: number; perPage: number }, isPaginated: boolean) => {
    handlePageChange(pageParams, isPaginated);
    clearSelection();
  };

  const sortState: SortState = { column: queryParams.sortBy ?? null, direction: queryParams.sortDir ?? null };
  const baseColumns = enableRidersAdvancedSorting ? getSortableColumns(sortState, handleSort) : invoiceGroupColumns;
  const checkboxColumn = getCheckboxColumn(
    pageRiderIds,
    selectedRiderIds,
    handleSelectAllOnPage,
    handleCheckboxSelection
  );
  const columns = showCheckboxes ? [checkboxColumn, ...baseColumns] : baseColumns;

  const categoriesKey = queryParams.categories?.join(',') ?? '';
  const safetySettingsKey = queryParams.safetySettings?.join(',') ?? '';
  const filterKey = `${queryParams.query}-${queryParams.state}-${queryParams.byRiderSearchAlpha}-${queryParams.invoiceGroupIds}-${queryParams.sortBy}-${queryParams.sortDir}-${categoriesKey}-${safetySettingsKey}`;

  const hasActiveFilters = (queryParams.categories?.length ?? 0) + (queryParams.safetySettings?.length ?? 0) > 0;
  const isFiltering = !!queryParams.query || !!queryParams.invoiceGroupIds || hasActiveFilters;

  return (
    <TablePaginationContainer
      totalCount={totalCount}
      handlePageChange={handlePageChangeWithReset}
      filterKey={filterKey}
      isFetching={isLoading}
      heightOffset={tableHeightOffset}
      initialPerPage={queryParams.perPage}
    >
      <DataTable
        stickyHeader={true}
        isLoading={isLoading}
        handleRowClick={handleRiderClick}
        href={({ slug }) => `/riders/${slug}`}
        columns={columns}
        rows={riders}
        noRecordsMessage={
          isError && !isLoading ? (
            <QueryErrorState
              message="Something went wrong loading riders. Please try again."
              tableHeightOffset={tableHeightOffset}
              onRetry={fetchRiders}
            />
          ) : (
            getNoRidersMessage(
              currentStatusTab,
              isFiltering,
              tableHeightOffset,
              hasActiveFilters,
              clearAllFilters,
              selectedLetter,
              onClearAlphabetFilter,
              isLoading
            )
          )
        }
      />
    </TablePaginationContainer>
  );
};

export const getNoRidersMessage = (
  currentTab: string,
  isFiltering: boolean,
  tableHeightOffset: number,
  hasActiveFilters = false,
  onClearFilters?: () => void,
  selectedLetter?: string,
  onClearAlphabetFilter?: () => void,
  isLoading = false
) => {
  if (isLoading) {
    return null;
  }
  const minHeight = window.innerHeight - tableHeightOffset - variables.tableHeadHeight;
  const isActiveTab = currentTab === 'active';

  if (selectedLetter && onClearAlphabetFilter) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="16px"
        minHeight={minHeight}
      >
        <Text textColor="secondaryText" textAlign="center">
          {`No ${currentTab} riders found for ${selectedLetter}`}
        </Text>
        <Button variant="text" onClick={onClearAlphabetFilter}>
          Clear letter filter
        </Button>
      </Box>
    );
  }

  if (hasActiveFilters && onClearFilters) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="16px"
        minHeight={minHeight}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: colorPalette.navy99,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 40,
            color: colorPalette.neutralVariant60,
            opacity: 0.5,
          }}
        >
          ⊘
        </Box>
        <Box
          sx={{
            color: colorPalette.navy20,
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 'normal',
          }}
        >
          No riders match your filters
        </Box>
        <Box
          sx={{
            color: colorPalette.neutralVariant60,
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 380,
            lineHeight: 1.5,
          }}
        >
          Try adjusting your filter selections or clearing all filters to see all riders.
        </Box>
        <Button
          color="tertiary"
          onClick={onClearFilters}
          sx={{ borderRadius: '6px', px: '16px', height: '36px', fontSize: 13, fontWeight: 600, minWidth: 144 }}
        >
          Clear all filters
        </Button>
      </Box>
    );
  }

  if (isFiltering) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={minHeight}>
        <Text textColor="secondaryText" textAlign="center">
          {'Sorry, no riders found for the selected filters. Please try with a different combination.'}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="1rem"
      justifyContent="center"
      alignItems="center"
      minHeight={minHeight}
    >
      <Text textColor="secondaryText" textAlign="center">
        {`Sorry, no riders have been ${isActiveTab ? 'added to' : 'deactivated for'} this account yet.`}
      </Text>
      {isActiveTab && (
        <Can I={actions.add} a={subjects.rider}>
          <CreateRider />
        </Can>
      )}
    </Box>
  );
};
